/// <reference types="node" />
// frontend/smartdocs-admin/src/components/settings/SystemHealthModal.tsx
import { useEffect, useState, useCallback } from 'react';
import { X, RefreshCw, Database, Cpu, MemoryStick, Clock, Server, type LucideIcon } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useSettings } from '@/context/SettingsContext';
import Card from "@shared/Card";
import ToggleSwitch from "@shared/ToggleSwitch";
import Button from "@shared/Button";

interface SystemHealthModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

interface HealthData {
  database: {
    status: string;
  };
  apiResponseTime: number;
  cpu: {
    message: string;
    metrics?: {
      loadavg: number[];
    };
  };
  memory: {
    metrics: {
      percent: number;
    };
  };
  uptime: string;
  diskUsage: string;
}

interface HistoryEntry extends HealthData {
  timestamp: string;
}

const SystemHealthModal: React.FC<SystemHealthModalProps> = ({ isOpen, onClose}) => {
  const { getSystemHealth } = useSettings();
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]); // To store historical data for charts

  const [isModal, setIsModal] = useState(false);

  // State for auto-refresh
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSystemHealth();
      if (data) {
        setHealthData(data);
        setHistory(prev => {
          const newEntry: HistoryEntry = { ...data, timestamp: new Date().toLocaleTimeString() };
          // Keep only the last 20 entries for the chart
          return [...prev.slice(-19), newEntry];
        });
      }
    } catch (err: any) {
      setError(err);
      console.error('Error fetching system health:', err);
    } finally {
      setLoading(false);
    }
    setLastRefreshed(new Date()); // Update last refreshed timestamp
  }, [getSystemHealth]);

  useEffect(() => {
    isOpen ? setIsModal(true) : setIsModal(false);
    fetchHealthData();
  }, [fetchHealthData]);

  // Auto-refresh effect
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    const REFRESH_INTERVAL = 60 * 1000; // 60 seconds

    const startRefresh = () => {
      if (autoRefresh && !document.hidden) {
        intervalId = setInterval(() => {
          fetchHealthData;
        }, REFRESH_INTERVAL);
      }
    };

    const stopRefresh = () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };

    // Start refresh when component mounts or autoRefresh is enabled
    startRefresh();

    // Listen for visibility changes to pause/resume refresh
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopRefresh();
      } else {
        startRefresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on component unmount or when autoRefresh changes
    return () => {
      stopRefresh();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoRefresh, fetchHealthData]); // Re-run effect if autoRefresh or fetchDashboardData changes

  const renderMetricCard = (title: string, value: string | number, unit: string = '', IconComponent?: LucideIcon) => ( // Corrected parameter syntax
    <Card key={title}>
      <div className="p-4 rounded-lg  flex items-center space-x-3">
        {IconComponent && <IconComponent className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
        <div>
          <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">{title}</h3>
          <p className="text-xl font-semibold text-gray-900 dark:text-white">{value}{unit}</p>
        </div>
      </div>
    </Card>
  );

  const renderChart = (dataKey: string, title: string, unit: string = '') => (
    <Card key={title}>
      <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200 mb-2">{title}</h3>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={history} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
          <XAxis dataKey="timestamp" tick={{ fill: '#888' }} />
          <YAxis tickFormatter={(value) => `${value}${unit}`} tick={{ fill: '#888' }} />
          <Tooltip formatter={(value) => [`${value}${unit}`, title]} />
          <Line type="monotone" dataKey={dataKey} stroke="#8884d8" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );

  const renderModalBody = () => (

    <div>
      {loading && (
        <Card>
        <div className="text-center text-gray-500 dark:text-gray-400 flex flex-col items-center justify-center h-full">
          <RefreshCw className="w-8 h-8 animate-spin mb-3" />
          Loading system health data...
        </div>
        </Card>
      )}
      {error && (
        <div className="text-red-500 text-center">Error: {error.message || 'Failed to load system health.'}</div>
      )}
      {healthData && !loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {renderMetricCard('Database Status', healthData.database.status, '', Database)}
            {renderMetricCard('API Response Time', healthData.apiResponseTime, 'ms', Clock)}
            {renderMetricCard('CPU Usage', healthData.cpu.message, '%', Cpu)}
            {renderMetricCard('Memory Usage', healthData.memory.metrics.percent.toFixed(1), '%', MemoryStick)}
            {renderMetricCard('Uptime', healthData.uptime, '', Server)}
            {renderMetricCard('Disk Usage', healthData.diskUsage, '%', Server)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderChart('cpu.metrics.loadavg[0]', 'CPU Usage', '%')}
            {renderChart('memory.metrics.percent', 'Memory Usage', '%')}
          </div>
        </div>
      )}
    </div>

  )

  return (

      isModal ? (
        <div className="fixed inset-0 bg-gray-600/75 dark:bg-gray-900/75 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">System Health Dashboard</h2>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">

            {renderModalBody()}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 text-right">
              <Button
                onClick={fetchHealthData}
                className="inline-flex items-center px-4 py-2  disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
                variant="primary"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      ) :
        (
          <div className="p-6 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">System Health Dashboard</h1>
            {/* Added items-end for right alignment on mobile, sm:items-center for center alignment on larger screens */}
            <div className="flex flex-col items-end sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              {/* Auto-refresh toggle */}
              <div className="flex items-center space-x-2">
                <ToggleSwitch
                  id="auto-refresh"
                  label="Auto-refresh"
                  checked={autoRefresh}
                  onChange={() => setAutoRefresh(!autoRefresh)}
                />
                {/*

                     isOn={autoRefresh} handleToggle={() => setAutoRefresh(!autoRefresh)}*/}

                {/* Manual Refresh Button */}
                <Button
                  onClick={fetchHealthData}
                  variant="secondary"
                  className="flex items-center space-x-2"
                  disabled={loading}
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </Button>
              </div>

              <div className="text-sm px-3 text-gray-500 md:order-first">
                {lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleString()}` : 'Loading...'}
              </div>
            </div>
          </div>
            {renderModalBody()}
          </div>
        )
  );
};

export default SystemHealthModal;
